/**
 * Signal watch derivation — the present-tense band state (canon 23 §4.1).
 *
 * Canon 23 v2 §4.1/§6.4: the band shows where the plan stands right now —
 * clear / watching / overdue — never a condition letter. The overdue count
 * comes from the signal_events_unanswered VIEW (the one truth source for
 * that predicate, doc 07); this function only summarizes, it never
 * recomputes the unanswered predicate client-side.
 */

import type { SignalEventRow } from '../../types/signal-events';

export type SignalWatchState = 'clear' | 'watching' | 'overdue';

export interface SignalWatchSummary {
  state: SignalWatchState;
  monitoringCount: number;
  confirmedCount: number;
  /** From the signal_events_unanswered view — never derived here. */
  unansweredCount: number;
}

export function deriveSignalWatch(
  signals: ReadonlyArray<Pick<SignalEventRow, 'status'>>,
  unansweredCount: number
): SignalWatchSummary {
  const monitoringCount = signals.filter((s) => s.status === 'monitoring').length;
  const confirmedCount = signals.filter((s) => s.status === 'confirmed').length;
  const state: SignalWatchState =
    unansweredCount > 0
      ? 'overdue'
      : monitoringCount + confirmedCount > 0
        ? 'watching'
        : 'clear';
  return { state, monitoringCount, confirmedCount, unansweredCount };
}

export const SIGNAL_WATCH_COPY: Record<
  SignalWatchState,
  { label: string; blurb: string }
> = {
  clear: {
    label: 'Holding — instruments clear',
    blurb: 'No open signals. Holding course is the disciplined state, not the idle one.',
  },
  watching: {
    label: 'Holding — on signal watch',
    blurb: 'Open signals are on the instruments. Each ages toward an answer: respond, keep monitoring, or dismiss with rationale.',
  },
  overdue: {
    label: 'Signals awaiting an answer',
    blurb: 'Confirmed signals are past their review date with no recorded response.',
  },
};
